import React from "react";

import { useTranslation } from "react-i18next";

import CommonTypeEditor from "./CommonTypeEditor";
import ImageFormatSelector from "./ImageFormatSelector";
import ShareConfig from "./section/ShareConfig";
import { COMPONENT_TYPES } from "../../../constants/componentType";
import {
  ADDITIONNAL_IMAGE_FORMATS,
  DEFAULT_IMAGE_FORMATS,
  IMAGE_FORMATS,
} from "../../../constants/ratio";
import { getComponentByType } from "../../../utils/data/Architecture";

import type { JSX } from "react";

import type {
  ImageFormatComponent,
  PlatformComponent,
  Section,
  ShareComponent,
} from "../../../api/ProjectAPI.types";
import type { ImageFormat } from "../../../constants/ratio";

type SmartlistPageSection = Extract<
  Section,
  "smartlist_page" | "collection_page"
>;

interface SmartlistPageEditorProps {
  components: PlatformComponent[];
  onChangeComponents: (components: PlatformComponent[]) => void;
  section: SmartlistPageSection;
}

const SECTION_LABELS: Record<
  SmartlistPageSection,
  { pageType: string; pictureRatio: string }
> = {
  smartlist_page: {
    pageType: "smartlist",
    pictureRatio: "pictureRatioSmartlist",
  },
  collection_page: {
    pageType: "collection",
    pictureRatio: "pictureRatioCollection",
  },
};

function SmartlistPageEditor({
  components,
  onChangeComponents,
  section,
}: SmartlistPageEditorProps): JSX.Element | null {
  const { t } = useTranslation("editor-platform");

  const { SHARE, CAROUSEL, MOSAIC, IMAGE_FORMAT } = COMPONENT_TYPES;
  const { FORMAT_16_6 } = IMAGE_FORMATS;
  const { pageType, pictureRatio } = SECTION_LABELS[section];

  const imageFormatComponent: ImageFormatComponent = getComponentByType(
    components,
    IMAGE_FORMAT,
  );

  const smartlistComponent =
    getComponentByType(components, MOSAIC) ??
    getComponentByType(components, CAROUSEL);
  const shareComponent = getComponentByType(components, SHARE);

  const onChangeShareConfig = (
    option: keyof ShareComponent["params"],
    value: boolean,
  ): void => {
    const updatedComponents = components.map((component) => {
      if (component.type === SHARE) {
        return {
          ...component,
          params: {
            ...component.params,
            [option]: value,
          },
        };
      }
      return component;
    });

    onChangeComponents(updatedComponents);
  };

  const handleChangeRatio = (ratio: ImageFormat): void => {
    const updatedComponents = components.map((component) => {
      if (component.type === IMAGE_FORMAT) {
        return {
          ...component,
          params: {
            ...component.params,
            ratio,
          },
        };
      }
      return component;
    });

    onChangeComponents(updatedComponents);
  };

  const handleChangeDisabledImage = (isDisabled: boolean): void => {
    const updatedComponents = components.map((component) => {
      if (component.type === IMAGE_FORMAT) {
        return {
          ...component,
          params: {
            ...component.params,
            isDisabled,
          },
        };
      }
      return component;
    });

    onChangeComponents(updatedComponents);
  };

  const imageFormatList: ImageFormat[] = [
    ...DEFAULT_IMAGE_FORMATS,
    ...ADDITIONNAL_IMAGE_FORMATS,
  ];

  const imageFormatSelected =
    imageFormatComponent?.params?.ratio || FORMAT_16_6;

  const isDisabledCoverPicture = imageFormatComponent?.params?.isDisabled;

  if (!smartlistComponent || !shareComponent || !imageFormatComponent)
    return null;

  return (
    <div className="editor-form-container editor-smartlist-page-container">
      <div className="editor-tile-container">
        <div className="pt-[0.625rem]">
          <ImageFormatSelector
            hasDisableSwitch
            imageFormatList={imageFormatList}
            isDisabled={isDisabledCoverPicture}
            onChangeDisabledImage={handleChangeDisabledImage}
            onChangeRatio={handleChangeRatio}
            selected={imageFormatSelected}
            title={t(pictureRatio)}
          />
        </div>
      </div>
      <CommonTypeEditor
        allowWidget
        components={components}
        imageFormatList={imageFormatList}
        onChangeComponents={onChangeComponents}
        pageType={pageType}
      >
        <ShareConfig
          onChangeOpt={onChangeShareConfig}
          shareComponent={shareComponent}
        />
      </CommonTypeEditor>
    </div>
  );
}

export default SmartlistPageEditor;
